import { gql } from '../../generated'

export const RELEVANT_INVOICE_DATA_SUBSCRIPTION = gql(/* GraphQL */ `
  subscription GetRelevantInvoiceData($companyGuid: uuid!) {
    companiesByPk(companyGuid: $companyGuid) {
      name
      companyConfig {
        blurb
        invoiceIncludePaymentLink
        invoiceDisplayTechnicianName
        invoiceIssueWorkflow
        maintenancePlanSavingsEnabled
        customerNumberEnabled
      }
      billingProfile {
        businessFullLegalName
        contractorLicenseNumber
        invoiceTerm
        logoUrl
        invoiceDisclaimer
        businessAddress {
          city
          line1
          line2
          zipCode
          stateAbbreviation
        }
        websiteDisplayName
        emailAddress {
          emailAddress
        }
        phoneNumber {
          phoneNumber
        }
        defaultPricebookTaxRateGuid
      }
      pricebookItems {
        pricebookItemGuid
      }
      accCompanyConfigs {
        accountingIntegrationType
        autosyncInvoiceOnIssued
        autosyncInvoiceOnPayment
        autosyncInvoiceOnFullyPaid
        autosyncInvoiceOnVoided
      }
    }
  }
`)

export const QUERY_RELEVANT_INVOICE_EDIT_ACCOUNT_DATA = gql(/* GraphQL */ `
  query QueryRelevantInvoiceEditAccountData($accountGuid: uuid!) {
    accountsByPk(accountGuid: $accountGuid) {
      accountType
      accountLocations(
        orderBy: { createdAt: ASC }
        where: { isArchived: { _eq: false } }
      ) {
        location {
          address {
            addressGuid
            city
            line1
            line2
            stateAbbreviation
            zipCode
          }
        }
        locationGuid
      }
      accountContacts(orderBy: { primary: DESC }, where: { archived: { _eq: false } }) {
        contact {
          contactGuid
          primaryEmailAddress {
            emailAddress
          }
          fullName
          primaryPhoneNumber {
            phoneNumber
          }
        }
      }
      mailingAddress {
        addressGuid
        city
        line1
        line2
        stateAbbreviation
        zipCode
      }
      accountReferenceNumber
      customerNumber
    }
  }
`)

export const QUERY_INVOICE_CHECKLISTS = gql(/* GraphQL */ `
  query InvoiceChecklists($invoiceGuid: uuid!) {
    invoicesByPk(invoiceGuid: $invoiceGuid) {
      jobLink {
        job {
          jobGuid
          appointments {
            jobAppointmentGuid
            appointmentWindowStart
            appointmentChecklistInstances {
              appointmentChecklistInstanceGuid
              name: checklist(path: "name")
              appointmentChecklist {
                customerFacingByDefault
              }
            }
            assignments {
              technician {
                firstName
                lastName
              }
            }
          }
        }
      }
    }
  }
`)

export const QUERY_RELEVANT_INVOICE_EDIT_JOB_DATA = gql(/* GraphQL */ `
  query QueryRelevantInvoiceEditJobData($jobGuid: uuid!) {
    jobsByPk(jobGuid: $jobGuid) {
      locationGuid
      location {
        addressGuid
        address {
          zipCode
        }
        pricebookTaxRate {
          pricebookTaxRateGuid
          name
          rate
        }
      }
      customerPurchaseOrderNumber
      appointments {
        jobAppointmentGuid
        appointmentWindowStart
      }
      pointOfContact {
        contactGuid
        fullName
        primaryEmailAddress {
          emailAddress
        }
        primaryPhoneNumber {
          phoneNumber
        }
      }
    }
  }
`)

export const RELEVANT_INVOICE_EDIT_ACCOUNT_DATA_SUBSCRIPTION = gql(/* GraphQL */ `
    subscription GetRelevantInvoiceEditAccountData($accountGuid: uuid!) {
      accountsByPk(accountGuid: $accountGuid) {
        accountType
        accountLocations(
          orderBy: { createdAt: ASC }
          where: { isArchived: { _eq: false } }
        ) {
          location {
            address {
              addressGuid
              city
              line1
              line2
              stateAbbreviation
              zipCode
            }
          }
          locationGuid
        }
        accountContacts(orderBy: { primary: DESC }, where: { archived: { _eq: false } }) {
          contact {
            contactGuid
            primaryEmailAddress {
              emailAddress
            }
            fullName
            primaryPhoneNumber {
              phoneNumber
            }
          }
        }
        mailingAddress {
          addressGuid
          city
          line1
          line2
          stateAbbreviation
          zipCode
        }
        defaultInvoiceTerm
        accountReferenceNumber
        customerNumber
      }
    }
  `)

export const RELEVANT_INVOICE_EDIT_JOB_DATA_SUBSCRIPTION = gql(/* GraphQL */ `
  subscription GetRelevantInvoiceEditJobData($jobGuid: uuid!) {
    jobsByPk(jobGuid: $jobGuid) {
      locationGuid
      location {
        addressGuid
        address {
          zipCode
        }
        pricebookTaxRate {
          pricebookTaxRateGuid
          name
          rate
        }
      }
      customerPurchaseOrderNumber
      appointments {
        jobAppointmentGuid
        appointmentWindowStart
      }
      pointOfContact {
        contactGuid
        fullName
        primaryEmailAddress {
          emailAddress
        }
        primaryPhoneNumber {
          phoneNumber
        }
      }
    }
  }
`)

export const UPSERT_INVOICE_MUTATION = gql(/* GraphQL */ `
  mutation UpsertInvoice(
    $invoiceGuid: uuid!
    $validCartItemGuids: [uuid!]!
    $validInvoiceDiscountGuids: [uuid!]!
    $invoice: InvoicesInsertInput!
  ) {
    insertInvoicesOne(
      object: $invoice
      onConflict: {
        constraint: invoices_pkey
        updateColumns: [
          billingAddressGuid
          billingContactGuid
          customerPurchaseOrderNumber
          displayId
          displayIdV2
          dueAt
          invoiceTerm
          messageHtml
          maintenancePlanSavingsEnabled
          serviceAddressGuid
          serviceCompletionDate
          status
          subtotalUsc
          taxRate
          taxRateGuid
          totalUsc
          taxRateName
          issuedAt
          updatedAt
          dynamicPricingType
        ]
      }
    ) {
      __typename
    }
    deleteCartItems(
      where: {
        invoiceCartItems: {
          invoiceGuid: { _eq: $invoiceGuid }
          cartItemGuid: { _nin: $validCartItemGuids }
        }
      }
    ) {
      __typename
    }
    deleteInvoiceDiscounts(
      where: {
        invoiceGuid: { _eq: $invoiceGuid }
        invoiceDiscountGuid: { _nin: $validInvoiceDiscountGuids }
      }
    ) {
      __typename
    }
  }
`)

export const UPDATE_INVOICE_MUTATION = gql(/* GraphQL */ `
  mutation UpdateInvoice($invoiceGuid: uuid!, $set: InvoicesSetInput!) {
    updateInvoicesByPk(pkColumns: { invoiceGuid: $invoiceGuid }, _set: $set) {
      __typename
    }
  }
`)

export const INVOICE_FRAGMENT = gql(/* GraphQL */ `
  fragment Invoice on Invoices {
    messageHtml
    status
    doNotSyncToAccountingServiceReason
    maintenancePlanSavingsEnabled
    companyGuid
    serviceAddress {
      city
      addressGuid
      line1
      line2
      stateAbbreviation
      zipCode
    }
    serviceCompletionDate
    taxRate
    taxRateName
    taxRateGuid
    invoiceTerm
    dynamicPricingType
    billingContact {
      fullName
      firstName
      contactGuid
      primaryPhoneNumber {
        phoneNumber
      }
      primaryEmailAddress {
        emailAddress
      }
    }
    billingAddress {
      city
      addressGuid
      line1
      line2
      stateAbbreviation
      zipCode
    }
    invoiceEvents(orderBy: { occurredAt: DESC }) {
      eventType
      title
      createdByUser {
        userGuid
        fullName
        firstName
        lastName
      }
      metadata
      occurredAt
    }
    invoiceDiscounts(orderBy: { seq: ASC }) {
      name
      discountType
      discountAmountUsc
      discountRate
      invoiceDiscountGuid
      descriptionHtml
    }
    invoiceCartItems(orderBy: { seq: ASC }) {
      seq
      cartItem {
        itemType: cartItemType
        description
        isDiscountable
        isTaxable
        name
        unitPriceUsc
        quantity
        cartItemGuid
        originalPricebookItemGuid
        cartItemCode
        cartItemVariant
        photo {
          cdnUrl
          photoGuid
        }
      }
    }
    jobLink {
      jobGuid
      job {
        createdAt
        displayId
        jobType {
          name
        }
        pointOfContact {
          firstName
          fullName
          lastName
          primaryPhoneNumber {
            phoneNumber
            type
          }
          primaryEmailAddress {
            emailAddress
          }
          contactGuid
        }
      }
    }
    maintenancePlanLink {
      maintenancePlanGuid
      maintenancePlan {
        activatedAt
        createdAt
        paymentFlow
        maintenancePlanDefinition {
          marketingInfo {
            name
          }
        }
        location {
          addressGuid
        }
      }
    }
    locationLink {
      locationGuid
      location {
        addressGuid
      }
    }
    estimateLink {
      estimate {
        estimateGuid
        displayId
        createdAt
        acceptedAt
      }
    }
    jobAppointmentLink {
      jobAppointment {
        jobAppointmentGuid
        appointmentType
        appointmentWindowStart
        assignments(orderBy: [{ createdAt: ASC }, { jobAppointmentAssignmentGuid: ASC }]) {
          technicianUserGuid
          technician {
            firstNameInitial
          }
        }
      }
    }
    accountGuid
    displayId
    displayIdV2
    dueAt
    issuedAt
    createdAt
    updatedAt
    customerPurchaseOrderNumber
    billingContactGuid
    signatureFileUrl
    account {
      createdAt
      accountReferenceNumber
      customerNumber
      accountContacts(where: { primary: { _eq: true } }) {
        contact {
          firstName
          fullName
          lastName
          primaryEmailAddress {
            emailAddress
          }
          primaryPhoneNumber {
            phoneNumber
            type
          }
          contactGuid
        }
      }
      mailingAddressGuid
    }
    invoicePayments(
      where: {
        paymentRecord: {
          paymentStatuses: { paymentStatus: { _nin: [CANCELED] } }
        }
      }
      orderBy: { paymentRecord: { occurredAt: ASC } }
    ) {
      paymentRecordGuid
      appliedAmountUsc
      invoice {
        displayIdV2
      }
      paymentRecord {
        paymentRecordGuid
        paymentMethod
        occurredAt
        paymentRefunds {
          amountUsd
          occurredAt
        }
        amountUsd
        paymentStatuses(
          orderBy: { occurredAt: DESC, createdAt: DESC }
          limit: 1
        ) {
          paymentStatus
        }
      }
    }
    qboStaleInfo {
      stale: isQboStale
      syncedAt: qboSyncedAt
      updatedAt
    }
    accountingStaleInfo {
      isStale
      syncedAt
      latestPaymentAt
      invoiceUpdatedAt
    }
    businessUnitLink {
      businessUnitGuid
      businessUnit {
        name
      }
    }
    taxConfiguration {
      taxRoundingStrategy
    }
  }
`)

export const INVOICE_FRAGMENT_WITH_GUID = gql(/* GraphQL */ `
  fragment InvoiceWithGuid on Invoices {
    invoiceGuid
    ...Invoice
  }
`)

export const INVOICE_DATA_SUBSCRIPTION = gql(/* GraphQL */ `
  subscription InvoiceData($invoiceGuid: uuid!) {
    invoicesByPk(invoiceGuid: $invoiceGuid) {
      ...Invoice
    }
  }
`)

export const INVOICES_DATA_SUBSCRIPTION = gql(/* GraphQL */ `
  subscription InvoicesData($where: InvoicesBoolExp) {
    invoices(where: $where) {
      ...InvoiceWithGuid
    }
  }
`)

export const INVOICES_DATA_QUERY = gql(/* GraphQL */ `
  query InvoicesDataQuery($where: InvoicesBoolExp) {
    invoices(where: $where) {
      ...InvoiceWithGuid
    }
  }
`)

export const MAINTENANCE_PLAN_DETAILS_QUERY = gql(/* GraphQL */ `
  query MaintenancePlanDetailsQuery($maintenancePlanGuid: uuid!) {
    maintenancePlansQueryableByPk(maintenancePlanGuid: $maintenancePlanGuid) {
      maintenancePlanDefinition {
        marketingInfo {
          name
        }
      }
    }
  }
`)
